import { useEffect, useRef } from 'react';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { takeUpsellCheckoutSurface } from './upsellCheckoutSurface';
import { trackPurchaseCompleted } from './watermarkUpsellAnalytics';

/**
 * Sends `purchase_completed` one time when the payment return page reaches its success state
 * (trial upsell spec 2026-10-13 §8: the first web caller of the event). `upsell_surface` is the
 * surface that started the checkout, kept in the tab across the Stripe trip; null for a checkout
 * that no upsell surface started.
 */
export function usePurchaseCompletedEvent(input: {
  isSuccess: boolean;
  plan: Music24PlanTier;
  /** The paid amount in cents and its currency, when the checkout session gave them. */
  amountTotal: number | null;
  currency: string | null;
}): void {
  const { isSuccess, plan, amountTotal, currency } = input;
  const sent = useRef(false);
  useEffect(() => {
    if (!isSuccess || sent.current) {
      return;
    }
    sent.current = true;
    trackPurchaseCompleted({
      plan,
      value: amountTotal === null ? undefined : amountTotal / 100,
      currency: currency ?? undefined,
      upsell_surface: takeUpsellCheckoutSurface(),
    });
  }, [isSuccess, plan, amountTotal, currency]);
}
